/* ==========================================================================
   ModaVeil — theme behaviour
   Custom elements only, no framework. Each element is self-contained so
   Shopify's section rendering can re-inject markup without re-binding.
   ========================================================================== */
(function () {
  'use strict';

  var Theme = window.Theme || {};

  /* ------------------------------------------------------------------------
     Focus trap — keeps Tab inside an open drawer/modal.
     ------------------------------------------------------------------------ */
  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function trapFocus(container, onEscape) {
    var previous = document.activeElement;

    function handler(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (onEscape) onEscape();
        return;
      }
      if (event.key !== 'Tab') return;

      var items = Array.prototype.filter.call(
        container.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null; }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handler);

    return function release() {
      document.removeEventListener('keydown', handler);
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }

  /* ------------------------------------------------------------------------
     Scroll lock — reference-counted so nested drawers don't unlock early.
     ------------------------------------------------------------------------ */
  var lockCount = 0;
  var savedScroll = 0;

  function lockScroll() {
    if (lockCount === 0) {
      savedScroll = window.scrollY;
      // Compensate for the scrollbar so the page doesn't shift on lock.
      var gap = window.innerWidth - document.documentElement.clientWidth;
      if (gap > 0) document.body.style.paddingInlineEnd = gap + 'px';
      document.body.dataset.scrollLocked = 'true';
    }
    lockCount++;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      delete document.body.dataset.scrollLocked;
      document.body.style.paddingInlineEnd = '';
      window.scrollTo(0, savedScroll);
    }
  }

  /* ------------------------------------------------------------------------
     <drawer-element> — generic slide-in panel (cart, menu, filters).
     ------------------------------------------------------------------------ */
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]') || this;
      this.overlay = this.querySelector('[data-drawer-overlay]');
      this.releaseFocus = null;

      this.onOverlayClick = this.close.bind(this);
      if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);

      this.querySelectorAll('[data-drawer-close]').forEach(
        function (btn) { btn.addEventListener('click', this.close.bind(this)); }.bind(this)
      );
    }

    disconnectedCallback() {
      if (this.releaseFocus) this.releaseFocus();
    }

    open() {
      if (this.panel.dataset.open === 'true') return;
      this.panel.dataset.open = 'true';
      if (this.overlay) this.overlay.dataset.open = 'true';
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      this.releaseFocus = trapFocus(this.panel, this.close.bind(this));

      var focusTarget = this.panel.querySelector('[data-drawer-focus]') ||
        this.panel.querySelector(FOCUSABLE);
      if (focusTarget) focusTarget.focus();

      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (this.panel.dataset.open !== 'true') return;
      this.panel.dataset.open = 'false';
      if (this.overlay) this.overlay.dataset.open = 'false';
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      if (this.releaseFocus) {
        this.releaseFocus();
        this.releaseFocus = null;
      }
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    toggle() {
      this.panel.dataset.open === 'true' ? this.close() : this.open();
    }
  }
  customElements.define('drawer-element', DrawerElement);

  /* Any button can open a drawer via data-drawer-open="DrawerId". */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    var drawer = document.getElementById(trigger.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open();
  });

  /* ------------------------------------------------------------------------
     Cart API
     ------------------------------------------------------------------------ */
  var Cart = {
    add: function (formData) {
      return fetch(Theme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData
      }).then(this._parse).then(function (data) {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        return data;
      });
    },

    change: function (key, quantity) {
      return fetch(Theme.routes.cartChange, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(this._parse).then(function (data) {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        return data;
      });
    },

    _parse: function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Cart request failed');
        }
        return data;
      });
    }
  };

  Theme.Cart = Cart;

  /* ------------------------------------------------------------------------
     <product-form> — AJAX add to cart.
     ------------------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.querySelector('[type="submit"]');
      this.errorTarget = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.button && this.button.getAttribute('aria-disabled') === 'true') return;

      this.setLoading(true);
      this.clearError();

      Cart.add(new FormData(this.form))
        .then(function () {
          var drawer = document.getElementById('CartDrawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        })
        .catch(this.showError.bind(this))
        .finally(this.setLoading.bind(this, false));
    }

    setLoading(isLoading) {
      if (!this.button) return;
      this.button.setAttribute('aria-disabled', String(isLoading));
      this.button.classList.toggle('is-loading', isLoading);
    }

    showError(error) {
      if (!this.errorTarget) return;
      this.errorTarget.textContent = error.message;
      this.errorTarget.hidden = false;
    }

    clearError() {
      if (!this.errorTarget) return;
      this.errorTarget.textContent = '';
      this.errorTarget.hidden = true;
    }
  }
  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------------------
     <quantity-input> — plus/minus stepper.
     ------------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.querySelectorAll('button[data-step]').forEach(
        function (button) {
          button.addEventListener('click', function () {
            var step = parseInt(button.dataset.step, 10);
            var min = parseInt(this.input.min, 10) || 0;
            var max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
            var next = (parseInt(this.input.value, 10) || 0) + step;

            this.input.value = String(Math.min(Math.max(next, min), max));
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }.bind(this));
        }.bind(this)
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------------
     Sticky header — adds .is-stuck past the hero, .is-hidden on scroll down.
     ------------------------------------------------------------------------ */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.ticking = false;
      this.onScroll = this.handleScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    handleScroll() {
      if (this.ticking) return;
      this.ticking = true;

      window.requestAnimationFrame(function () {
        var y = window.scrollY;
        var threshold = this.offsetHeight;

        this.classList.toggle('is-stuck', y > threshold);
        // Only hide once well past the header, and never while a drawer is open.
        if (!document.body.dataset.scrollLocked) {
          this.classList.toggle('is-hidden', y > threshold * 3 && y > this.lastY);
        }

        this.lastY = y;
        this.ticking = false;
      }.bind(this));
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ------------------------------------------------------------------------
     <carousel-element> — scroll-snap track with prev/next buttons.
     Scrolls by one page rather than one card, and reads its own direction
     from the document so RTL "next" moves the correct way.
     ------------------------------------------------------------------------ */
  class CarouselElement extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');

      if (this.prev) this.prev.addEventListener('click', this.scrollBy.bind(this, -1));
      if (this.next) this.next.addEventListener('click', this.scrollBy.bind(this, 1));

      this.onScroll = this.updateButtons.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.updateButtons();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    scrollBy(direction) {
      // In RTL, scrollLeft runs negative, so "forward" is the opposite sign.
      var sign = Theme.rtl ? -1 : 1;
      this.track.scrollBy({
        left: direction * sign * this.track.clientWidth,
        behavior: 'smooth'
      });
    }

    updateButtons() {
      // Math.abs normalises RTL's negative scrollLeft.
      var pos = Math.abs(this.track.scrollLeft);
      var max = this.track.scrollWidth - this.track.clientWidth;
      var atStart = pos <= 1;
      var atEnd = pos >= max - 1;

      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
      this.updateDots(pos, max);
    }

    /* Optional page dots: one per viewport-width of track. Rebuilt only when
       the page count changes (resize), otherwise just re-marked. */
    updateDots(pos, max) {
      var dots = this.querySelector('[data-carousel-dots]');
      if (!dots) return;
      var width = this.track.clientWidth || 1;
      var pages = max > 1 ? Math.ceil(max / width) + 1 : 1;
      var active = pos >= max - 1 ? pages - 1 : Math.round(pos / width);

      if (dots.children.length !== pages) {
        dots.innerHTML = '';
        for (var i = 0; i < pages; i++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel-dot';
          dot.setAttribute('aria-label', String(i + 1) + ' / ' + String(pages));
          dot.addEventListener('click', this.goTo.bind(this, i));
          dots.appendChild(dot);
        }
      }
      dots.hidden = pages < 2;
      Array.prototype.forEach.call(dots.children, function (dot, i) {
        dot.setAttribute('aria-current', String(i === active));
      });
    }

    goTo(page) {
      var sign = Theme.rtl ? -1 : 1;
      this.track.scrollTo({ left: page * sign * this.track.clientWidth, behavior: 'smooth' });
    }
  }
  customElements.define('carousel-element', CarouselElement);

  /* ------------------------------------------------------------------------
     <localization-form> — country/language switcher submits on change.
     ------------------------------------------------------------------------ */
  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-localization-select]').forEach(function (select) {
        select.addEventListener('change', function () {
          select.form.submit();
        });
      });
    }
  }
  customElements.define('localization-form', LocalizationForm);

  /* ------------------------------------------------------------------------
     Cart drawer — re-rendered from the server after every cart mutation.
     ------------------------------------------------------------------------ */
  function refreshCartDrawer() {
    var section = document.getElementById('CartDrawerSection');
    if (!section) return Promise.resolve();

    return fetch(Theme.routes.root + '?sections=cart-drawer')
      .then(function (response) { return response.json(); })
      .then(function (data) {
        var markup = data['cart-drawer'];
        if (!markup) return;

        // Preserve open state across the swap — the drawer is usually open
        // precisely because something was just added to it.
        var wasOpen = section.querySelector('[data-drawer-panel][data-open="true"]') !== null;

        var parsed = new DOMParser().parseFromString(markup, 'text/html');
        var fresh = parsed.getElementById('CartDrawerSection');
        if (!fresh) return;

        section.innerHTML = fresh.innerHTML;

        if (wasOpen) {
          var drawer = document.getElementById('CartDrawer');
          // The old element's lock was released when it left the DOM.
          if (drawer && typeof drawer.open === 'function') {
            unlockScroll();
            drawer.open();
          }
        }
      });
  }

  function updateCartCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  }

  /* ------------------------------------------------------------------------
     Cart page — re-rendered from the server after every cart mutation.

     The drawer was refreshed and the page was not, so changing a quantity on
     /cart left the line total and the subtotal showing the numbers from page
     load. The cart was right; only the page was stale, which is the worst
     version of the bug because the first correct figure appeared at checkout.
     ------------------------------------------------------------------------ */
  function refreshCartPage() {
    var section = document.querySelector('.main-cart-section');
    if (!section || !section.id) return Promise.resolve();

    // JSON template, so the id carries the theme's own number
    // (template--21535716737114__main) and cannot be written down here.
    var id = section.id.replace(/^shopify-section-/, '');

    var active = document.activeElement;
    var focusKey = active && active.hasAttribute
      && active.hasAttribute('data-quantity-input') ? active.dataset.lineKey : null;

    return fetch(Theme.routes.cart + '?sections=' + encodeURIComponent(id))
      .then(function (response) { return response.json(); })
      .then(function (data) {
        var markup = data[id];
        if (!markup) return;

        var parsed = new DOMParser().parseFromString(markup, 'text/html');
        var fresh = parsed.getElementById(section.id);
        if (!fresh) return;

        section.innerHTML = fresh.innerHTML;

        // The input being typed into has just been replaced. Put the caret
        // back, or the field loses focus on every step and the next click on
        // + lands on an element that no longer exists.
        if (focusKey) {
          var again = section.querySelector(
            '[data-quantity-input][data-line-key="' + focusKey + '"]');
          if (again) {
            again.focus();
            try {
              again.setSelectionRange(again.value.length, again.value.length);
            } catch (error) {
              /* number inputs refuse setSelectionRange in some browsers */
            }
          }
        }
      })
      .catch(function () {
        /* Leave the page as it is rather than blanking the cart on a hiccup. */
      });
  }

  document.addEventListener('cart:updated', function (event) {
    var cart = event.detail;
    // add.js returns the line item, not the cart, so re-read the count.
    if (cart && typeof cart.item_count === 'number') {
      updateCartCount(cart);
      refreshCartDrawer();
      refreshCartPage();
    } else {
      fetch(Theme.routes.cart + '.js')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          updateCartCount(data);
          refreshCartPage();
          return refreshCartDrawer();
        });
    }
  });

  /* One cart write at a time. The stepper dispatches a change per click, and
     overlapping writes to the same line let the slower response win. */
  var cartQueue = Promise.resolve();

  function queueCartChange(key, quantity) {
    cartQueue = cartQueue
      .then(function () { return Cart.change(key, quantity); })
      .catch(function () { /* a failed write must not stall the queue */ });
    return cartQueue;
  }

  /* Quantity changes and removals, in the drawer and on the cart page. */
  var pendingQuantity = {};

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-quantity-input]');
    if (!input) return;

    var key = input.dataset.lineKey;
    var quantity = parseInt(input.value, 10) || 0;

    // Three quick taps on + are one intention, not three. Collapse them.
    clearTimeout(pendingQuantity[key]);
    pendingQuantity[key] = setTimeout(function () {
      delete pendingQuantity[key];
      queueCartChange(key, quantity);
    }, 250);
  });

  document.addEventListener('click', function (event) {
    var remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();

    // Removal is immediate - no debounce, but it still queues behind any
    // quantity write already in flight for the same line.
    clearTimeout(pendingQuantity[remove.dataset.lineKey]);
    delete pendingQuantity[remove.dataset.lineKey];
    queueCartChange(remove.dataset.lineKey, 0);
  });

  /* ------------------------------------------------------------------------
     Country auto-selection.

     Shopify tells us the visitor's likely country; if we sell there and it is
     not what is already selected, switch once. A choice made by hand is
     permanent and is never overridden.
     ------------------------------------------------------------------------ */
  var COUNTRY_KEY = 'modaveil:country';

  function countryFlag() {
    try { return localStorage.getItem(COUNTRY_KEY); } catch (error) { return 'unavailable'; }
  }

  function setCountryFlag(value) {
    try { localStorage.setItem(COUNTRY_KEY, value); } catch (error) { /* private mode */ }
  }

  /* Any hand-made choice stops the automatic one, for good. */
  document.addEventListener('change', function (event) {
    var field = event.target.closest && event.target.closest('[name="country_code"]');
    if (field) setCountryFlag('manual');
  });

  function submitCountry(code) {
    var form = document.createElement('form');
    form.method = 'post';
    form.action = Theme.routes.root.replace(/\/$/, '') + '/localization';
    form.style.display = 'none';

    var fields = {
      form_type: 'localization',
      utf8: '\u2713',
      _method: 'put',
      country_code: code,
      return_to: location.pathname + location.search
    };
    Object.keys(fields).forEach(function (name) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = fields[name];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  function autoSelectCountry() {
    if (countryFlag()) return;               // manual, already done, or no storage

    var field = document.querySelector('[name="country_code"]');
    if (!field) return;                      // single-country store: nothing to pick

    var current = field.value;
    var offered = Array.prototype.map.call(
      document.querySelectorAll('[name="country_code"] option, [name="country_code"]'),
      function (el) { return el.value; }
    );

    fetch(Theme.routes.root + 'browsing_context_suggestions.json?country[enabled]=true')
      .then(function (response) { return response.json(); })
      .then(function (data) {
        var detected = data && data.detected_values && data.detected_values.country;
        var code = detected && detected.handle;

        // Nothing to do, but record that we looked - otherwise every page load
        // spends a request re-asking the same question.
        if (!code || code === current || offered.indexOf(code) === -1) {
          setCountryFlag('checked');
          return;
        }

        // Written BEFORE submitting: a failed switch must not loop.
        setCountryFlag('auto:' + code);
        submitCountry(code);
      })
      .catch(function () { setCountryFlag('checked'); });
  }

  autoSelectCountry();

  /* ------------------------------------------------------------------------
     Wishlist — kept on the device.
     Stores product handles (not ids) so the wishlist page can render each
     card through the Section Rendering API without an app. Emits
     'wishlist:updated' so an account-backed wishlist app can take over later.
     ------------------------------------------------------------------------ */
  var WISHLIST_KEY = 'modaveil:wishlist';

  function readWishlist() {
    try {
      var list = JSON.parse(localStorage.getItem(WISHLIST_KEY));
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(handles) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
    } catch (error) {
      /* Private mode — the in-page state still works for this session. */
    }
  }

  function paintWishlist() {
    var handles = readWishlist();
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (button) {
      button.setAttribute('aria-pressed', String(handles.indexOf(button.dataset.productHandle) !== -1));
    });
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = handles.length;
      el.hidden = handles.length === 0;
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();

    var handle = button.dataset.productHandle;
    if (!handle) return;
    var handles = readWishlist();
    var index = handles.indexOf(handle);

    index === -1 ? handles.push(handle) : handles.splice(index, 1);
    writeWishlist(handles);
    paintWishlist();

    document.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: { handle: handle, added: index === -1, handles: handles }
    }));
  });

  document.addEventListener('DOMContentLoaded', paintWishlist);
  document.addEventListener('shopify:section:load', paintWishlist);

  /* <wishlist-grid> — the wishlist page. Each saved handle is rendered by the
     'wishlist-card' section in that product's context, so the cards are the
     same Liquid as everywhere else. A product that was deleted or unpublished
     answers 404 and is dropped from the list. */
  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-wishlist-list]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.render();
      this.onUpdate = this.render.bind(this);
      document.addEventListener('wishlist:updated', this.onUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:updated', this.onUpdate);
    }

    render() {
      var handles = readWishlist();
      var root = Theme.routes.root.replace(/\/$/, '');
      var self = this;

      this.empty.hidden = handles.length > 0;
      if (handles.length === 0) {
        this.list.innerHTML = '';
        return;
      }

      Promise.all(handles.map(function (handle) {
        return fetch(root + '/products/' + encodeURIComponent(handle) + '?section_id=wishlist-card')
          .then(function (response) { return response.ok ? response.text() : null; })
          .catch(function () { return null; });
      })).then(function (results) {
        var alive = [];
        var html = '';
        results.forEach(function (markup, i) {
          if (!markup) return;
          alive.push(handles[i]);
          var doc = new DOMParser().parseFromString(markup, 'text/html');
          var card = doc.querySelector('.product-card');
          if (card) html += '<li>' + card.outerHTML + '</li>';
        });
        if (alive.length !== handles.length) writeWishlist(alive);
        self.list.innerHTML = html;
        self.empty.hidden = alive.length > 0;
        paintWishlist();
      });
    }
  }
  customElements.define('wishlist-grid', WishlistGrid);

  /* ------------------------------------------------------------------------
     <announcement-rotator> — one message at a time with prev/next arrows.
     Auto-advances, pauses on hover/focus, and stays still for reduced motion.
     ------------------------------------------------------------------------ */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      if (this.slides.length < 2) return;
      this.index = 0;

      var prev = this.querySelector('[data-rotator-prev]');
      var next = this.querySelector('[data-rotator-next]');
      if (prev) prev.addEventListener('click', this.go.bind(this, -1));
      if (next) next.addEventListener('click', this.go.bind(this, 1));

      this.paused = false;
      this.addEventListener('mouseenter', function () { this.paused = true; }.bind(this));
      this.addEventListener('mouseleave', function () { this.paused = false; }.bind(this));
      this.addEventListener('focusin', function () { this.paused = true; }.bind(this));
      this.addEventListener('focusout', function () { this.paused = false; }.bind(this));

      var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var seconds = parseInt(this.dataset.interval, 10) || 5;
      if (!still) {
        this.timer = setInterval(function () {
          if (!this.paused) this.go(1);
        }.bind(this), seconds * 1000);
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    go(step) {
      if (this.closest('.announcement-bar--spread') && window.matchMedia('(min-width: 990px)').matches) return;
      this.slides[this.index].hidden = true;
      this.index = (this.index + step + this.slides.length) % this.slides.length;
      this.slides[this.index].hidden = false;
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ------------------------------------------------------------------------
     <hero-slideshow> — cross-fading slides with arrows and dots.
     Auto-advances unless switched off, pauses on hover/focus and while the
     tab is hidden, and never moves for reduced motion. Hidden slides are
     inert so their links drop out of the tab order.
     ------------------------------------------------------------------------ */
  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-slideshow-dot]'));
      if (this.slides.length < 2) return;
      this.index = 0;

      var prev = this.querySelector('[data-slideshow-prev]');
      var next = this.querySelector('[data-slideshow-next]');
      if (prev) prev.addEventListener('click', this.step.bind(this, -1));
      if (next) next.addEventListener('click', this.step.bind(this, 1));
      this.dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          this.show(parseInt(dot.dataset.slideshowDot, 10));
          this.restart();
        }.bind(this));
      }, this);

      // Swipe on touch screens. In RTL a leftward swipe means "previous".
      this.addEventListener('touchstart', function (e) {
        this.touchX = e.touches[0].clientX;
      }.bind(this), { passive: true });
      this.addEventListener('touchend', function (e) {
        if (this.touchX == null) return;
        var dx = e.changedTouches[0].clientX - this.touchX;
        this.touchX = null;
        if (Math.abs(dx) < 40) return;
        var forward = dx < 0;
        if (Theme.rtl) forward = !forward;
        this.step(forward ? 1 : -1);
      }.bind(this), { passive: true });

      this.paused = false;
      this.addEventListener('mouseenter', function () { this.paused = true; }.bind(this));
      this.addEventListener('mouseleave', function () { this.paused = false; }.bind(this));
      this.addEventListener('focusin', function () { this.paused = true; }.bind(this));
      this.addEventListener('focusout', function () { this.paused = false; }.bind(this));

      // The theme editor selects a block: show that slide and hold still.
      document.addEventListener('shopify:block:select', this.onBlockSelect = function (e) {
        var i = this.slides.indexOf(e.target);
        if (i === -1) return;
        this.show(i);
        this.paused = true;
      }.bind(this));

      this.restart();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    restart() {
      clearInterval(this.timer);
      var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.dataset.autoplay !== 'true' || still) return;
      var seconds = parseInt(this.dataset.interval, 10) || 6;
      this.timer = setInterval(function () {
        if (!this.paused && !document.hidden) this.show(this.index + 1);
      }.bind(this), seconds * 1000);
    }

    step(direction) {
      this.show(this.index + direction);
      this.restart();
    }

    show(i) {
      var n = this.slides.length;
      i = (i + n) % n;
      this.slides.forEach(function (slide, j) {
        var on = j === i;
        slide.toggleAttribute('data-active', on);
        slide.toggleAttribute('inert', !on);
        if (on) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
        // Lazy images on later slides load just before they are needed.
        if (on || j === (i + 1) % n) {
          slide.querySelectorAll('img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
        }
      });
      this.dots.forEach(function (dot, j) {
        dot.setAttribute('aria-current', String(j === i));
      });
      this.index = i;
    }
  }
  customElements.define('hero-slideshow', HeroSlideshow);

  /* ------------------------------------------------------------------------
     <media-gallery> — thumbnail switches the active image.
     ------------------------------------------------------------------------ */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.thumbs = this.querySelectorAll('[data-media-target]');
      if (!this.main || !this.thumbs.length) return;

      this.thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', this.select.bind(this, thumb.dataset.mediaTarget));
      }.bind(this));
    }

    select(mediaId, quiet) {
      this.querySelectorAll('[data-media-id]').forEach(function (media) {
        var active = media.dataset.mediaId === mediaId;
        media.toggleAttribute('data-inactive', !active);
        // Mobile lays the media out as a scroll rail, where nothing is hidden;
        // scrolling is the equivalent of switching there. `quiet` is for the
        // sync on first paint - a page that scrolls itself while still loading
        // reads as broken, and it fights scroll restoration.
        if (active && !quiet && window.matchMedia('(max-width: 749px)').matches) {
          media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });

      this.thumbs.forEach(function (thumb) {
        thumb.setAttribute('aria-current', String(thumb.dataset.mediaTarget === mediaId));
      });
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ------------------------------------------------------------------------
     <variant-picker>
     Resolves the selected option values to a variant, then re-renders the
     price from the server so currency and locale formatting stay correct.
     ------------------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-variant-data]');
      if (!data) return;

      try {
        this.variants = JSON.parse(data.textContent);
      } catch (error) {
        return;
      }

      this.addEventListener('change', this.onChange.bind(this));
      this.updateAvailability();

      // A shared link can carry ?variant=, and the picker itself writes one on
      // every change. Without this the page would open on the default photo
      // while the brown swatch sits selected beside it.
      var current = this.matchVariant(this.selectedOptions);
      if (current) this.updateGallery(current, true);
    }

    /* The variant whose options are exactly these, or undefined. */
    matchVariant(selected) {
      return this.variants.find(function (v) {
        return selected.every(function (value, i) { return v.options[i] === value; });
      });
    }

    get selectedOptions() {
      return Array.prototype.map.call(
        this.querySelectorAll('input[type="radio"]:checked'),
        function (input) { return input.value; }
      );
    }

    onChange() {
      var selected = this.selectedOptions;
      var variant = this.matchVariant(selected);

      this.updateLabels(selected);
      this.updateAvailability();
      this.updateButton(variant);
      this.updateInput(variant);

      if (!variant) return;

      this.updateUrl(variant);
      this.updatePrice(variant);
      this.updateGallery(variant);
    }

    /* Show the chosen colour's photo.

       featured_media, not featured_image: the gallery keys its elements on
       media ids, and a variant's featured_image carries an image id from a
       different numbering. Matching on that finds nothing and fails silently,
       which is indistinguishable from doing nothing at all. */
    updateGallery(variant, quiet) {
      var media = variant.featured_media;
      if (!media || !media.id) return;

      var section = this.closest('.shopify-section') || document;
      var gallery = section.querySelector('media-gallery');
      // The element may not be upgraded yet on first paint.
      if (!gallery || typeof gallery.select !== 'function') return;

      gallery.select(String(media.id), quiet);
    }

    updateLabels(selected) {
      this.querySelectorAll('[data-option-value]').forEach(function (el, i) {
        if (selected[i]) el.textContent = selected[i];
      });
    }

    /* Grey out values that cannot combine with the current selection. */
    updateAvailability() {
      var selected = this.selectedOptions;

      this.querySelectorAll('input[type="radio"]').forEach(function (input) {
        var position = parseInt(input.dataset.optionPosition, 10) - 1;
        var candidate = selected.slice();
        candidate[position] = input.value;

        var match = this.variants.find(function (v) {
          // Only compare the options chosen so far, ignoring later ones.
          return candidate.every(function (value, i) {
            return i > position && i >= selected.length ? true : v.options[i] === value;
          });
        });

        var label = input.nextElementSibling;
        if (!label) return;
        label.toggleAttribute('data-unavailable', !match || !match.available);
      }.bind(this));
    }

    updateButton(variant) {
      var button = document.querySelector('[data-add-button]');
      var text = document.querySelector('[data-add-button-text]');
      if (!button || !text) return;

      if (!variant) {
        button.setAttribute('aria-disabled', 'true');
        text.textContent = button.dataset.textUnavailable;
      } else if (!variant.available) {
        button.setAttribute('aria-disabled', 'true');
        text.textContent = button.dataset.textSoldOut;
      } else {
        button.removeAttribute('aria-disabled');
        text.textContent = button.dataset.textAdd;
      }
    }

    updateInput(variant) {
      var input = document.querySelector('[data-variant-id]');
      if (!input) return;
      input.value = variant ? variant.id : '';
      input.disabled = !variant || !variant.available;
    }

    updateUrl(variant) {
      // Keeps the URL shareable and the back button meaningful.
      window.history.replaceState({}, '', this.dataset.url + '?variant=' + variant.id);
    }

    updatePrice(variant) {
      var url = this.dataset.url + '?variant=' + variant.id +
        '&section_id=' + this.dataset.section;

      fetch(url)
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-price-target]');
          var current = document.querySelector('[data-price-target]');
          if (fresh && current) current.innerHTML = fresh.innerHTML;

          var freshStock = parsed.querySelector('[data-low-stock]');
          var currentStock = document.querySelector('[data-low-stock]');
          if (currentStock) {
            currentStock.textContent = freshStock ? freshStock.textContent : '';
            currentStock.hidden = !freshStock;
          }
        })
        .catch(function () { /* Price stays as-is; the cart is still correct. */ });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------------
     <related-products> — fetches recommendations once it nears the viewport.
     ------------------------------------------------------------------------ */
  class RelatedProducts extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;

      var observer = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        this.load();
      }.bind(this), { rootMargin: '300px' });

      observer.observe(this);
    }

    load() {
      fetch(this.dataset.url)
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('related-products');
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            paintWishlist();
          }
        }.bind(this))
        .catch(function () { /* Recommendations are optional; fail quietly. */ });
    }
  }
  customElements.define('related-products', RelatedProducts);

  /* ------------------------------------------------------------------------
     <facet-filters> — filtering and sorting without a full page load.
     Both the sidebar and the drawer contain a copy of the same form, so
     changes are read from whichever form the event came from.
     ------------------------------------------------------------------------ */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.results = this.querySelector('[data-facet-results]');
      if (!this.results) return;

      this.debounce = null;

      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));

      // Restore the right results when the user hits back.
      this.onPop = function (event) {
        if (event.state && event.state.facets) this.render(location.href, false);
      }.bind(this);
      window.addEventListener('popstate', this.onPop);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
    }

    onChange(event) {
      var form = event.target.closest('[data-facet-form]');
      if (!form) return;

      var isText = event.target.type === 'number';
      // Price inputs fire on every keystroke; checkboxes should feel instant.
      window.clearTimeout(this.debounce);
      this.debounce = window.setTimeout(this.submit.bind(this, form), isText ? 500 : 0);
    }

    onClick(event) {
      var chip = event.target.closest('.facets__chip, .facets__clear, .pagination__link');
      if (!chip || !chip.getAttribute('href')) return;
      event.preventDefault();
      this.render(chip.href, true);
    }

    submit(form) {
      var params = new URLSearchParams(new FormData(form));

      // Drop empty values so the URL stays readable and shareable.
      Array.from(params.keys()).forEach(function (key) {
        if (params.get(key) === '') params.delete(key);
      });

      var base = window.location.pathname;
      var query = params.toString();
      this.render(query ? base + '?' + query : base, true);
    }

    render(url, push) {
      this.results.setAttribute('aria-busy', 'true');

      fetch(url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + this.dataset.section)
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');

          var freshResults = parsed.querySelector('[data-facet-results]');
          if (freshResults) this.results.innerHTML = freshResults.innerHTML;

          // Facet counts and availability change with the selection, so the
          // panels are replaced too — not just the product grid.
          var freshPanels = parsed.querySelectorAll('[data-facet-panel]');
          var panels = this.querySelectorAll('[data-facet-panel]');
          freshPanels.forEach(function (fresh, i) {
            if (panels[i]) panels[i].innerHTML = fresh.innerHTML;
          });

          if (push) {
            window.history.pushState({ facets: true }, '', url);
          }

          paintWishlist();
          this.results.removeAttribute('aria-busy');
          this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }.bind(this))
        .catch(function () {
          // Fall back to a normal navigation so filtering always works.
          window.location.href = url;
        });
    }
  }
  customElements.define('facet-filters', FacetFilters);

  Theme.refreshCartDrawer = refreshCartDrawer;
  window.Theme = Theme;
})();
